// LocationAutocomplete - Airbnb-style location search with Google Places autocomplete
// UI/UX optimiert: Bessere Hover-States, Fokus-Differenzierung, Accessibility, Mobile
import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { MapPin, Navigation, Clock, Loader2, X } from 'lucide-react';
import { googleMapsService, type PlacePrediction, type GeocodedLocation } from '../../services/googleMapsService';

interface LocationAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (location: GeocodedLocation) => void;
  placeholder?: string;
  showNearbyButton?: boolean;
  onNearbyClick?: () => void;
  isLocating?: boolean;
  nearbyAddress?: string;
  className?: string;
  inputClassName?: string;
  disabled?: boolean;
}

// Recent searches storage key
const RECENT_SEARCHES_KEY = 'goodplace_recent_locations';
const MAX_RECENT_SEARCHES = 5;

interface RecentSearch {
  query: string;
  location: GeocodedLocation;
  timestamp: number;
}

function getRecentSearches(): RecentSearch[] {
  try {
    const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load recent searches:', e);
  }
  return [];
}

function saveRecentSearch(query: string, location: GeocodedLocation): void {
  try {
    const recent = getRecentSearches();
    // Remove duplicate if exists
    const filtered = recent.filter(
      (r) => r.location.placeId !== location.placeId && r.query.toLowerCase() !== query.toLowerCase()
    );
    // Add new search at the beginning
    filtered.unshift({ query, location, timestamp: Date.now() });
    // Keep only max entries
    const trimmed = filtered.slice(0, MAX_RECENT_SEARCHES);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(trimmed));
  } catch (e) {
    console.error('Failed to save recent search:', e);
  }
}

export function LocationAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Ort, Stadtteil oder Region...',
  showNearbyButton = true,
  onNearbyClick,
  isLocating = false,
  nearbyAddress,
  className = '',
  inputClassName = '',
  disabled = false,
}: LocationAutocompleteProps) {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [apiAvailable, setApiAvailable] = useState(true);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Unique ID for ARIA
  const listboxId = useId();

  // Check if API is available
  useEffect(() => {
    setApiAvailable(googleMapsService.isAvailable());
  }, []);

  // Load recent searches on mount
  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  // Fetch predictions with debounce
  const fetchPredictions = useCallback(async (input: string) => {
    if (!apiAvailable || input.length < 2) {
      setPredictions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const results = await googleMapsService.getPlacePredictions(input);
      setPredictions(results);
    } catch (error) {
      console.error('Autocomplete error:', error);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [apiAvailable]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length >= 2) {
      setIsLoading(true);
      debounceRef.current = setTimeout(() => {
        fetchPredictions(value);
      }, 300);
    } else {
      setPredictions([]);
      setIsLoading(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, fetchPredictions]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle prediction selection
  const handleSelectPrediction = async (prediction: PlacePrediction) => {
    setIsLoading(true);
    try {
      const location = await googleMapsService.getPlaceDetails(prediction.placeId);
      if (location) {
        onChange(prediction.mainText);
        onSelect(location);
        saveRecentSearch(prediction.mainText, location);
        setRecentSearches(getRecentSearches());
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
    } finally {
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  // Handle recent search selection
  const handleSelectRecent = (recent: RecentSearch) => {
    onChange(recent.query);
    onSelect(recent.location);
    setIsOpen(false);
  };

  // Handle nearby click
  const handleNearbyClick = () => {
    if (onNearbyClick) {
      onNearbyClick();
      setIsOpen(false);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const totalItems = (showNearbyButton ? 1 : 0) + recentSearches.length + predictions.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < totalItems - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : totalItems - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0) {
          let currentIndex = 0;
          if (showNearbyButton) {
            if (selectedIndex === currentIndex) {
              handleNearbyClick();
              return;
            }
            currentIndex++;
          }
          // Predictions kommen ZUERST (neue Reihenfolge)
          for (const prediction of predictions) {
            if (selectedIndex === currentIndex) {
              handleSelectPrediction(prediction);
              return;
            }
            currentIndex++;
          }
          // Recent Searches kommen DANACH
          for (const recent of recentSearches) {
            if (selectedIndex === currentIndex) {
              handleSelectRecent(recent);
              return;
            }
            currentIndex++;
          }
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // Clear input
  const handleClear = () => {
    onChange('');
    setPredictions([]);
    inputRef.current?.focus();
  };

  const showDropdown = isOpen && (showNearbyButton || recentSearches.length > 0 || predictions.length > 0 || isLoading);

  // Helper for item styling with better hover and focus states
  const getItemClassName = (isSelected: boolean, variant: 'nearby' | 'recent' | 'prediction') => {
    const base = 'w-full flex items-center gap-3 py-4 sm:py-3 transition-all text-left';

    if (isSelected) {
      // Fokus-State: Deutlich erkennbar mit linkem Akzent
      if (variant === 'nearby') {
        return `${base} bg-indigo-50 border-l-4 border-l-indigo-500 pl-3 pr-4`;
      }
      if (variant === 'recent') {
        return `${base} bg-amber-50/70 border-l-4 border-l-amber-400 pl-3 pr-4`;
      }
      return `${base} bg-gray-100 border-l-4 border-l-gray-400 pl-3 pr-4`;
    }

    // Hover-State: Sichtbarer als vorher
    if (variant === 'nearby') {
      return `${base} px-4 hover:bg-indigo-50/60 hover:shadow-sm hover:shadow-indigo-100/50`;
    }
    if (variant === 'recent') {
      return `${base} px-4 hover:bg-amber-50/40`;
    }
    return `${base} px-4 hover:bg-gray-100`;
  };

  return (
    <div className={`relative ${className}`}>
      {/* Input Field with ARIA attributes */}
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          // ARIA attributes for accessibility
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          aria-activedescendant={selectedIndex >= 0 ? `${listboxId}-option-${selectedIndex}` : undefined}
          aria-autocomplete="list"
          className={`w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed ${inputClassName}`}
        />
        {/* Loading/Clear button */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {isLoading ? (
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" aria-hidden="true" />
          ) : value ? (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded-full transition"
              aria-label="Eingabe löschen"
            >
              <X className="w-4 h-4 text-gray-400" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Dropdown with ARIA listbox role */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          aria-label="Ortsvorschläge"
          className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden max-h-64 sm:max-h-80 md:max-h-96 overflow-y-auto"
        >
          {/* Nearby Option */}
          {showNearbyButton && (
            <button
              type="button"
              id={`${listboxId}-option-0`}
              role="option"
              aria-selected={selectedIndex === 0}
              onClick={handleNearbyClick}
              disabled={isLocating}
              className={getItemClassName(selectedIndex === 0, 'nearby')}
            >
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                {isLocating ? (
                  <Loader2 className="w-5 h-5 text-indigo-600 animate-spin" aria-hidden="true" />
                ) : (
                  <Navigation className="w-5 h-5 text-indigo-600" aria-hidden="true" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900">In meiner Nähe</div>
                <div className="text-sm text-gray-500 truncate">
                  {isLocating
                    ? 'Standort wird ermittelt...'
                    : nearbyAddress || 'Aktuellen Standort verwenden'}
                </div>
              </div>
            </button>
          )}

          {/* Google Predictions ZUERST (neue Reihenfolge) */}
          {predictions.length > 0 && (
            <>
              {predictions.map((prediction, index) => {
                const itemIndex = (showNearbyButton ? 1 : 0) + index;
                return (
                  <button
                    key={prediction.placeId}
                    type="button"
                    id={`${listboxId}-option-${itemIndex}`}
                    role="option"
                    aria-selected={selectedIndex === itemIndex}
                    onClick={() => handleSelectPrediction(prediction)}
                    className={getItemClassName(selectedIndex === itemIndex, 'prediction')}
                  >
                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 truncate">{prediction.mainText}</div>
                      <div className="text-sm text-gray-500 truncate">{prediction.secondaryText}</div>
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {/* Skeleton Loading State */}
          {isLoading && predictions.length === 0 && value.length >= 2 && (
            <div className="divide-y divide-gray-100">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                  <div className="w-5 h-5 bg-gray-200 rounded-full flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-1.5" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Recent Searches DANACH (neue Reihenfolge) */}
          {recentSearches.length > 0 && (
            <>
              <div className="px-4 py-2 text-xs font-medium text-amber-700 uppercase tracking-wide bg-amber-50/60 border-t border-amber-100/50">
                Letzte Suchen
              </div>
              {recentSearches.map((recent, index) => {
                const itemIndex = (showNearbyButton ? 1 : 0) + predictions.length + index;
                return (
                  <button
                    key={recent.location.placeId || index}
                    type="button"
                    id={`${listboxId}-option-${itemIndex}`}
                    role="option"
                    aria-selected={selectedIndex === itemIndex}
                    onClick={() => handleSelectRecent(recent)}
                    className={getItemClassName(selectedIndex === itemIndex, 'recent')}
                  >
                    <Clock className="w-5 h-5 text-amber-500 flex-shrink-0" aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-900 truncate">{recent.query}</div>
                      {recent.location.city && recent.location.city !== recent.query && (
                        <div className="text-sm text-gray-500 truncate">{recent.location.city}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {/* No Results */}
          {!isLoading && value.length >= 2 && predictions.length === 0 && apiAvailable && (
            <div className="px-4 py-4 text-center text-gray-500 text-sm">
              Keine Ergebnisse für &quot;{value}&quot;
            </div>
          )}

          {/* API Not Available */}
          {!apiAvailable && value.length >= 2 && (
            <div className="px-4 py-4 text-center text-gray-500 text-sm">
              Ortsuche nicht verfügbar. Bitte manuell eingeben.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default LocationAutocomplete;
